import Button from '@components/atoms/button';
import { H2 } from '@components/atoms/typography';
import { WarningAlert } from '@components/alert';
import { TeamImportPanel } from '@components/my-team/TeamImportPanel';
import { useImportFromFpl } from '@hooks/my-team/useImportFromFpl';

const GATE_DESCRIPTION =
  'Now the season is under way, this page needs your FPL team. Enter your ' +
  'FPL ID or search by team name to link it - you only need to do this once.';

const GATE_HELP_TEXT =
  'Your FPL ID is the number in the web address when you open the Points ' +
  'tab on the official FPL site.';

type FplLinkGateProps = {
  onImported?: (fplTeamId: number) => void;
};

/**
 * Full-page, non-dismissable "link your FPL team" state shown in place of
 * page content when useFplLinkGate resolves to 'gate'. Linking persists
 * fplTeamId to the profile and re-imports the planner; the mutation's cache
 * invalidation refetches the profile, so the gate clears reactively without
 * a manual refresh.
 */
export const FplLinkGate = ({ onImported }: FplLinkGateProps) => {
  const importFromFpl = useImportFromFpl();

  const handleImport = async (fplTeamId: number) => {
    await importFromFpl.mutateAsync(fplTeamId);
    onImported?.(fplTeamId);
  };

  return (
    <div className="flex justify-center py-16 px-2" data-cy="fpl-link-gate">
      <div className="w-full max-w-[500px] flex flex-col gap-4 border border-neutral-200 rounded-xl py-6 px-4 md:p-8">
        <H2 className="text-center">Link your FPL team</H2>
        <TeamImportPanel
          onImport={handleImport}
          isImporting={importFromFpl.isLoading}
          description={GATE_DESCRIPTION}
          helpText={GATE_HELP_TEXT}
        />
      </div>
    </div>
  );
};

type FplLinkGateProfileErrorProps = {
  onRetry: () => void;
};

/**
 * Shown instead of the link prompt when the profile fetch fails: a failed
 * fetch leaves fplTeamId empty for a possibly-linked user, so prompting a
 * re-link would be wrong.
 */
export const FplLinkGateProfileError = ({
  onRetry
}: FplLinkGateProfileErrorProps) => (
  <div
    className="flex flex-col items-center gap-4 py-16 px-4"
    data-cy="fpl-link-gate-error"
  >
    <WarningAlert text="We couldn't load your profile. Please try again." />
    <Button variant="secondary" onClick={onRetry}>
      Try again
    </Button>
  </div>
);
